import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { useAppSelector, useAppTranslation } from "@/utils";
import { bnplMerchantApis } from "@payping/api";
import { CuToman } from "@payping/icons";
import {
  Flex,
  LegacyCard,
  LegacyForm,
  Select,
  Spin,
  Typography,
  useMessagedAsync,
  usePaypingContext,
} from "@payping/web-components";
import moment from "jalali-moment";
import { FC, useMemo, useRef } from "react";
import { Area, ComposedChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { dashboardPreferencesAtom } from "@/core/state";
import { selectAtom } from "jotai/utils";
import { useAtom } from "jotai";
import { LANGUAGES } from "@payping/utilities-shared/consts";

const languageAtom = selectAtom(dashboardPreferencesAtom, (val) => val.language);

type Data = { day: string; value: number };

interface Filters {
  month: number;
  year: number;
}

const CustomTooltip = ({ active, payload }: any) => {
  const { t } = useAppTranslation();
  if (active && payload && payload?.length) {
    const { value } = payload[0];
    return (
      <div className="tw:rounded tw:bg-color-text-base tw:px-3! tw:py-1! tw:shadow">
        <Typography.Text className="tw:text-color-white!">{`${formatLocalizedNumber({ value: value * 10000000, options: { maximumFractionDigits: 2 } })} ${t("common.tomans")}`}</Typography.Text>
      </div>
    );
  }
  return <div></div>;
};

interface ChartProps {
  creditCode: string;
  availableCredit: number;
  totalCredit: number;
}

/* eslint-disable react-hooks/refs */
const BnplManageCreditChartPage: FC<ChartProps> = ({ creditCode, availableCredit, totalCredit }) => {
  const { t } = useAppTranslation();

  const [language] = useAtom(languageAtom);
  const momentNow = useRef(moment());
  const [form] = LegacyForm.useForm<Filters>();

  const { breakpoints } = usePaypingContext();
  const { xs } = breakpoints;

  const momentTime = useRef<Record<typeof language, { year: number; month: number; day: number }>>({
    en: {
      year: momentNow.current.year(),
      month: momentNow.current.month(),
      day: momentNow.current.date(),
    },
    fa: {
      year: momentNow.current.jYear(),
      month: momentNow.current.jMonth(),
      day: momentNow.current.jDate(),
    },
  });

  const momentTimeLocal = momentTime.current[language];

  const registerDateTime = useAppSelector((state) => state.user.registerDateTime);

  const startYear = useMemo(() => {
    if (registerDateTime) {
      return language === LANGUAGES.ENGLISH ? moment(registerDateTime).year() : moment(registerDateTime).jYear();
    }
    return momentTimeLocal.year - 15;
  }, [registerDateTime, language, momentTimeLocal]);

  const yearsRange = useMemo(() => {
    const years = [];
    for (let year = startYear; year <= momentTimeLocal.year; year += 1) {
      years.push(year);
    }
    return years;
  }, [startYear, momentTimeLocal]);

  const monthWatch = LegacyForm.useWatch("month", form);
  const yearWatch = LegacyForm.useWatch("year", form);

  const availableMonths = useMemo(() => {
    const months =
      language === LANGUAGES.FARSI
        ? (t("common.solarMonths", { returnObjects: true }) as string[])
        : (t("common.gregorianMonths", { returnObjects: true }) as string[]);

    const currentYear = momentTimeLocal.year;
    const currentMonth = momentTimeLocal.month;

    const selectedYear = yearWatch ?? currentYear;

    return months
      .map((item, index) => ({ label: item, value: index }))
      .filter((_, index) => selectedYear < currentYear || index <= currentMonth);
  }, [language, t, yearWatch, momentTimeLocal]);

  const getChartState = useMessagedAsync(
    () =>
      bnplMerchantApis.getCreditChartData(
        creditCode,
        yearWatch ?? momentTimeLocal.year,
        (monthWatch ?? momentTimeLocal.month) + 1,
      ),

    [creditCode, momentTimeLocal.month, momentTimeLocal.year, monthWatch, yearWatch],
    { success: false },
  );

  const data = useMemo(() => {
    if (getChartState.value?.creditChartData?.length)
      return getChartState.value?.creditChartData?.map(
        (item, index) =>
          ({ day: item.date?.toString() ?? (index + 1).toString(), value: item.amount ?? 0 }) satisfies Data,
      );

    return Array.from({ length: 30 }).map((_, index) => ({ day: (index + 1).toString(), value: 0 }) satisfies Data);
  }, [getChartState.value]);

  const maxValue = totalCredit;

  return (
    <LegacyCard.Ant size="small" className="tw:w-full">
      <Flex vertical gap={"large"}>
        <Flex align={xs ? "end" : "center"} justify="space-between" vertical={xs} gap={"middle"}>
          <Flex vertical gap={"middle"}>
            <Typography.Text type="secondary">{t("shop.bnpl.manageCredit.remainLimit")}</Typography.Text>
            <Flex align="center">
              <Typography.Title level={5} className="tw:m-0!">
                {`${formatLocalizedNumber({ value: availableCredit, options: { maximumFractionDigits: 2 } })}`}
              </Typography.Title>
              <CuToman width={24} height={24} />
            </Flex>
          </Flex>
          <div />
          <Flex>
            <LegacyForm<Filters>
              className="tw:w-full"
              form={form}
              initialValues={{
                month: momentTimeLocal.month,
                year: momentTimeLocal.year,
              }}
            >
              <Flex justify="space-between" align="center" gap={"small"}>
                <Flex align="center" gap={"small"}>
                  <LegacyForm.Item<Filters> name={"month"} noStyle>
                    <Select className="tw:w-28" options={availableMonths} />
                  </LegacyForm.Item>
                  <LegacyForm.Item<Filters> name={"year"} noStyle>
                    <Select
                      className="tw:w-24"
                      options={yearsRange.map((item) => ({
                        label: item,
                        value: item,
                      }))}
                    />
                  </LegacyForm.Item>
                </Flex>
              </Flex>
            </LegacyForm>
          </Flex>
        </Flex>
        {getChartState.loading ? (
          <Flex className="tw:h-48" justify="center" align="center">
            <Spin />
          </Flex>
        ) : (
          <Flex vertical>
            <div className="tw:h-75 tw:w-full tw:overflow-auto">
              <div className="tw:h-full tw:w-full tw:min-w-141.75">
                <ResponsiveContainer width="100%" height="95%">
                  <ComposedChart data={data} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                    <XAxis dataKey="day" padding={{ left: 24, right: 24 }} />
                    <YAxis
                      padding={{ top: 24 }}
                      direction="ltr"
                      domain={[0, maxValue]}
                      tickFormatter={(val) => `${Math.round((val / maxValue) * 100)}%`}
                      ticks={[0, 0.2 * maxValue, 0.4 * maxValue, 0.6 * maxValue, 0.8 * maxValue, maxValue]}
                    />
                    <Tooltip content={CustomTooltip} />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="#0057ff"
                      fill="#0057ff22"
                      activeDot={{
                        r: 6,
                        fill: "#fff",
                        stroke: "#0057ff",
                        strokeWidth: 2,
                      }}
                    />
                    <ReferenceLine y={maxValue} stroke="#CF1322" strokeDasharray="5 5" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
            <Flex align="center" gap={"large"}>
              <Flex align="center" gap={"small"}>
                <div className="tw:h-1 tw:w-3 tw:rounded-lg tw:bg-color-primary" />
                <Typography.Text>{t("shop.bnpl.manageCredit.useagedLimit")}</Typography.Text>
              </Flex>
              <Flex align="center" gap={"small"}>
                <Flex align="center" gap={"2px"}>
                  <div className="tw:h-1 tw:w-2 tw:rounded-lg tw:bg-color-error" />
                  <div className="tw:h-1 tw:w-2 tw:rounded-lg tw:bg-color-error" />
                </Flex>
                <Typography.Text>{t("shop.bnpl.manageCredit.currentLimit")}</Typography.Text>
              </Flex>
            </Flex>
          </Flex>
        )}
      </Flex>
    </LegacyCard.Ant>
  );
};

export default BnplManageCreditChartPage;
